import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { PATHROUTES } from '@/constants/path'
import { showSuccessToast, showErrorToast } from '@/components/ui/toast'
import { DocumentIcon } from '@/components/Icons'
import EventBasicInfoForm from '@/components/event/EventBasicInfoForm'
import EventLocationForm from '@/components/event/EventLocationForm'
import EventRegistrationForm from '@/components/event/EventRegistrationForm'
import CustomRichTextEditor from '@/components/CustomRichTextEditor'
import {
  createEvent,
  getCategory,
  getEventById,
  getTag,
  updateEvent,
  type createEventPayload,
  type EventGuestUser,
  type EventHighlight,
} from '@/api/eventApi'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { EVENT_QUERY_KEYS, TAG_QUERY_KEYS } from '@/api/queryKeys'
import EventDateTimeForm from '@/components/event/EventDateTimeForm'
import EventAddUserForm from '@/components/event/EventAddUserForm'
import CustomLoader from '@/components/CustomLoader'
import type { EventDataType } from '@/schemas/event.schema'
import useImageUpload from '@/hooks/useImageUpload'
import axios from 'axios'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { extractEventTags } from '@/utils/helper'
import {
  eventFormSchema,
  type EventFormDataType,
} from '@/schemas/eventAdminEventForm.schema'
import {
  defaultActivityHighlights,
  defaultEventTickets,
  defaultEventUsers,
  defaultSponsorshipPackages,
  validateDates,
  type EventSponsorShipPayloadType,
} from '@/schemas/eventForm.schema'
import EventPackageForm, {
  type EventPackageFormRef,
} from '@/components/event/EventPackageForm'
import {
  cleanSpecialInstructions,
  EVENT_FORM_STATUS,
  extractSponsorShipPlan,
  getEventHighLightData,
  getFreeTicket,
  registrationOptions,
  transformEventHighlights,
  transformEventTickets,
  transformSponsorShipPlan,
} from '@/constants/event'
import {
  validateDraftGuests,
  validatePaidEventTickets,
  setGuestErrors,
  setPaidEventTicketErrors,
  buildDraftPayload,
  validateDraftPackages,
  setPackageErrors,
} from '@/utils/eventDraftHelper'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog'
import { combineDateTime } from '@/utils/dateUtils'
import EventPreviewDrawer from '@/components/event/EventPreviewDrawer'
import usePdfUpload from '@/hooks/usePDFUpload'
import {
  loadExistingPdfDocument,
  createHandleSelectPDF,
  createHandleRemovePDF,
  preparePdfPayload,
} from '@/utils/pdfHelper'

export interface EventUser {
  id: string
  fullName: string
  designation: string
  profile?: string
  avatarFile?: File
  isNew?: boolean
}

export interface EventTicket {
  id?: string
  name: string
  price: number | undefined
  description: string
  perks: string
  isNew?: boolean
  isSubEventTicket: boolean
  maxAttendeesLimit: number
}

export interface EventImage {
  id?: string
  key: string
  url?: string
  index?: number
  imageFile: File
  isNew?: boolean
}

export default function EventAdminAddEditEvent() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id } = useParams<{ id: string }>()
  const eventPackageFormRef = useRef<EventPackageFormRef>(null)

  const form = useForm<EventFormDataType>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: {
      eventName: '',
      description: '',
      category: '',
      tag: [],
      eventImages: [],
      eventPDF: undefined,
      eventInfoPDF: undefined,
      timeZone: '',
      location: 'online',
      zoomLink: '',
      venueName: '',
      venueAddress: '',
      venueCity: '',
      venueState: '',
      venueCountry: '',
      venueLatitude: undefined,
      venueLongitude: undefined,
      registrationType: 'free',
      eventTicket: defaultEventTickets,
      specialInstructions: '',
      users: defaultEventUsers,
      sponsorshipPackages: defaultSponsorshipPackages,
      eventHighlights: defaultActivityHighlights,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
  })

  const { handleSubmit, getValues } = form

  const { showDialog, handleConfirm, handleCancel, allowNavigation } =
    useUnsavedChanges(form)

  const validateFormDates = useCallback(
    (isSubmitting = false) => {
      const startDate = form.getValues('startDate')
      const startTime = form.getValues('startTime')
      const endDate = form.getValues('endDate')
      const endTime = form.getValues('endTime')
      const registrationDeadline = form.getValues('registrationDeadline')
      const mode = id ? 'edit' : 'add'
      const touchedFields = form.formState.touchedFields

      const errors = validateDates(
        startDate,
        startTime,
        endDate,
        endTime,
        registrationDeadline,
        mode,
        touchedFields,
        isSubmitting
      )

      form.clearErrors('startDate')
      form.clearErrors('startTime')
      form.clearErrors('endDate')
      form.clearErrors('endTime')
      form.clearErrors('registrationDeadline')

      Object.entries(errors).forEach(([field, message]) => {
        if (message) {
          form.setError(field as keyof EventFormDataType, {
            type: 'manual',
            message,
          })
        }
      })

      return Object.keys(errors).length === 0
    },
    [form, id]
  )

  const watchedFields = form.watch([
    'startDate',
    'startTime',
    'endDate',
    'endTime',
    'registrationDeadline',
  ])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      validateFormDates(false)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [...watchedFields, validateFormDates])

  const {
    uploadFiles: uploadEventImages,
    isUploading: isUploadingEventImages,
    getExistingImageFiles: getExistingEventImages,
  } = useImageUpload({
    linkFor: 'eventImages',
    maxFiles: 10,
  })
  const {
    uploadFiles: uploadGuestImage,
    isUploading: isUploadingGuestImages,
    getExistingImageFiles: getExistingGuestImages,
  } = useImageUpload({
    linkFor: 'eventGuestProfiles',
    maxFiles: 5,
  })
  const {
    uploadFiles: uploadHighlightImages,
    isUploading: isUploadingHighlightImages,
    getExistingImageFiles: getExistingEventHighLightImages,
  } = useImageUpload({
    linkFor: 'activityHighlights',
    maxFiles: 10,
  })

  const {
    uploadPdf: uploadEventPdf,
    isUploading: isUploadingEventPdf,
    getExistingPdfFile: getExistingEventPdfFile,
  } = usePdfUpload({
    linkFor: 'eventScheduleDocument',
  })

  const {
    uploadPdf: uploadEventInfoPdf,
    isUploading: isUploadingEventInfoPdf,
    getExistingPdfFile: getExistingEventInfoPdfFile,
  } = usePdfUpload({
    linkFor: 'eventInfoDocument',
  })

  const [isDataLoading, setIsDataLoading] = useState(false)

  const { data: eventData, isLoading: isLoadingEvent } = useQuery({
    queryKey: EVENT_QUERY_KEYS.byId(id!),
    queryFn: () => getEventById(id!),
    enabled: !!id,
    refetchOnWindowFocus: false,
  })

  const { data: categoryData } = useQuery({
    queryKey: EVENT_QUERY_KEYS.categories,
    queryFn: getCategory,
  })

  const { data: tagData } = useQuery({
    queryKey: TAG_QUERY_KEYS.all,
    queryFn: getTag,
  })

  const data: EventDataType = eventData?.data

  useEffect(() => {
    const loadData = async () => {
      if (data) {
        setIsDataLoading(true)

        try {
          const eventImagesPromises = data.images.map(async image => {
            const eventImage = await getExistingEventImages([image])
            return {
              id: image.id,
              key: image.key,
              url: image.url,
              index: image.index,
              imageFile: eventImage[0],
            }
          })
          const eventGuestsPromises =
            data.guests && data.guests.length > 0
              ? data.guests.map(async guest => {
                  const guestImage = await getExistingGuestImages([
                    guest.profile,
                  ])
                  return {
                    id: guest.id,
                    fullName: guest.name,
                    designation: guest.designation,
                    profile: guest.profile.key,
                    avatarFile: guestImage[0],
                  }
                })
              : defaultEventUsers

          const eventImages = await Promise.all(eventImagesPromises)
          const eventGuests = await Promise.all(eventGuestsPromises)

          // Load existing PDF document if present
          const eventPDFData = await loadExistingPdfDocument(
            data.document,
            getExistingEventPdfFile
          )

          // Load existing About Park City PDF document if present
          const eventInfoPDFData = await loadExistingPdfDocument(
            data.eventInfoDocument,
            getExistingEventInfoPdfFile
          )

          const packages = extractSponsorShipPlan(data.sponsorshipPlans)
          const eventHighLights = await getEventHighLightData(
            data?.activityHighlights,
            getExistingEventHighLightImages
          )

          const startDateTime = data.startDateTime
            ? new Date(data.startDateTime)
            : undefined
          const endDateTime = data.endDateTime
            ? new Date(data.endDateTime)
            : undefined

          form.reset({
            ...data,
            eventName: data.name,
            category: data.category?.id,
            tag: extractEventTags(data.tags),
            eventPDF: eventPDFData,
            eventInfoPDF: eventInfoPDFData,
            eventImages: eventImages,
            startDate: startDateTime,
            startTime: startDateTime,
            endDate: endDateTime,
            endTime: endDateTime,
            registrationDeadline: data.registrationDeadline
              ? new Date(data.registrationDeadline)
              : undefined,
            zoomLink: data.zoomLink || '',
            venueName: data.venueName || '',
            venueAddress: data.venueAddress || '',
            venueCity: data.venueCity || '',
            venueState: data.venueState || '',
            venueCountry: data.venueCountry || '',
            venueLatitude: Number(data.venueLatitude),
            venueLongitude: Number(data.venueLongitude),
            registrationType: data.type,
            maxAttendees: data.maxAttendeesLimit,
            ...getFreeTicket(data.type, data.tickets?.[0]),
            users: eventGuests,
            eventTicket:
              data.type === 'paid'
                ? transformEventTickets(data.tickets) || defaultEventTickets
                : defaultEventTickets,
            sponsorshipPackages: packages,
            eventHighlights: eventHighLights,
            specialInstructions: cleanSpecialInstructions(
              data.specialInstructions || ''
            ),
          })

          setTimeout(() => {
            validateFormDates(false)
          }, 100)
        } catch (error) {
          console.error('Error loading event data:', error)
          showErrorToast('Failed to load event data')
        } finally {
          setIsDataLoading(false)
        }
      }
    }

    loadData()
  }, [eventData])

  // Image upload handlers that work with React Hook Form
  const handleImageSelect = async (
    files: File | File[] | null,
    type: 'eventImage' | 'guestImage' | 'highlightImage',
    id?: string | number
  ) => {
    if (type === 'eventImage' && Array.isArray(files)) {
      const currentImages = getValues('eventImages') || []
      const newImages = files.map(file => ({
        key: '',
        imageFile: file,
        isNew: true,
      }))
      const updatedImages = [...currentImages, ...newImages].slice(0, 10)
      form.setValue('eventImages', updatedImages)

      if (updatedImages.length > 0) {
        form.clearErrors('eventImages')
      }

      const keys = await uploadEventImages(files)
      if (keys) {
        const currentImagesAfterUpload = getValues('eventImages') || []
        const finalImages = currentImagesAfterUpload.map((img, idx) =>
          idx >= currentImagesAfterUpload.length - files.length && !img.key
            ? {
                ...img,
                key: keys[
                  idx - (currentImagesAfterUpload.length - files.length)
                ],
              }
            : img
        )
        form.setValue('eventImages', finalImages)
        form.trigger('eventImages')
      }
    } else if (type === 'guestImage' && files && !Array.isArray(files)) {
      const currentUsers = getValues('users') || []
      const updatedUsers = currentUsers.map(user =>
        user.id === id ? { ...user, avatarFile: files } : user
      )
      form.setValue('users', updatedUsers)
      const userIndex = currentUsers.findIndex(user => user.id === id)
      if (userIndex !== -1) {
        form.clearErrors(`users.${userIndex}.avatarFile`)
      }

      const keys = await uploadGuestImage([files])
      if (keys?.[0]) {
        const currentUsersAfterUpload = getValues('users') || []
        const finalUsers = currentUsersAfterUpload.map(user =>
          user.id === id ? { ...user, profile: keys[0] } : user
        )
        form.setValue('users', finalUsers)

        if (userIndex !== -1) {
          form.trigger(`users.${userIndex}`)
        }
      }
    } else if (
      type === 'highlightImage' &&
      files &&
      !Array.isArray(files) &&
      typeof id === 'number'
    ) {
      // Store the File object without uploading immediately
      const currentHighlights = form.watch('eventHighlights') || []
      const updatedHighlights = currentHighlights.map((highlight, idx) =>
        idx === id
          ? { ...highlight, imageFile: files, eventImage: null }
          : highlight
      )
      form.setValue('eventHighlights', updatedHighlights)

      if (id >= 0 && id < currentHighlights.length) {
        form.clearErrors(`eventHighlights.${id}.eventImage`)
      }
    }
  }

  const handleRemoveImage = (index: number) => {
    const currentImages = getValues('eventImages') || []
    const updatedImages = currentImages.filter((_, i) => i !== index)
    form.setValue('eventImages', updatedImages)
  }

  // PDF upload handlers
  const handleSelectPDF = createHandleSelectPDF(form, 'eventPDF')
  const handleRemovePDF = createHandleRemovePDF(form, 'eventPDF')

  const handleSelectInfoPDF = createHandleSelectPDF(form, 'eventInfoPDF')
  const handleRemoveInfoPDF = createHandleRemovePDF(form, 'eventInfoPDF')

  const transformFormData = async (
    formData: EventFormDataType
  ): Promise<createEventPayload> => {
    const allEventImages: Pick<EventImage, 'id' | 'key'>[] =
      formData.eventImages.map(image => {
        const newImageData: Pick<EventImage, 'id' | 'key'> = {
          key: image.key,
        }

        if (id && !image.isNew) {
          newImageData.id = image.id
        }
        return newImageData
      })

    const guestUsers: EventGuestUser[] = formData.users
      .filter(user => {
        return !(
          !user.fullName &&
          !user.avatarFile &&
          !user.designation &&
          !user.profile
        )
      })
      .map(user => {
        const newUserData: EventGuestUser = {
          name: user.fullName,
          designation: user.designation,
          profile: user.profile ?? '',
        }

        if (id && !user.isNew) {
          newUserData.id = user.id
        }
        return newUserData
      })

    let eventTicketData: EventTicket[] = []
    if (formData.registrationType === 'paid') {
      eventTicketData = formData.eventTicket.map(ticket => {
        const newTicket: EventTicket = {
          name: ticket.name,
          description: ticket.description,
          perks: ticket.perks,
          price: Number(Number(ticket.price ?? 0).toFixed(6)),
          maxAttendeesLimit: Number(ticket.maxAttendeesLimit),
          isSubEventTicket: ticket.isSubEventTicket ?? false,
        }

        if (id && !ticket.isNew) {
          newTicket.id = ticket.id
        }
        return newTicket
      })
    } else if (formData.registrationType === 'free') {
      eventTicketData = formData?.eventTicket.map(ticket => {
        const newTicket: EventTicket = {
          name: formData?.ticketName,
          description: formData?.ticketDescription,
          perks: formData?.ticketPerks,
          price: 0,
          maxAttendeesLimit: formData?.maxAttendees,
          isSubEventTicket: ticket?.isSubEventTicket ?? false,
        }

        if (id && data?.type === registrationOptions[0].id) {
          newTicket.id = data?.tickets[0].id
        }

        return newTicket
      })
    }
    const packagePlans = form.getValues('sponsorshipPackages')
    const sponsorshipPackages: EventSponsorShipPayloadType[] =
      transformSponsorShipPlan(packagePlans)

    const eventHighlights: EventHighlight[] = await transformEventHighlights(
      formData.eventHighlights,
      uploadHighlightImages,
      !!id
    )

    const startDateTime = combineDateTime(
      formData.startDate,
      formData.startTime
    )
    const endDateTime = combineDateTime(formData.endDate, formData.endTime)

    const specialInstructions =
      cleanSpecialInstructions(formData.specialInstructions) || undefined

    const eventDocumentPayload = await preparePdfPayload(
      formData.eventPDF,
      uploadEventPdf
    )

    const eventInfoDocumentPayload = await preparePdfPayload(
      formData.eventInfoPDF,
      uploadEventInfoPdf
    )

    return {
      name: formData.eventName,
      categoryId: formData.category,
      description: formData.description,
      images: allEventImages,
      ...(eventDocumentPayload !== undefined
        ? { document: eventDocumentPayload }
        : {}),
      ...(eventInfoDocumentPayload !== undefined
        ? { eventInfoDocument: eventInfoDocumentPayload }
        : {}),
      endDateTime: endDateTime?.toISOString() ?? '',
      startDateTime: startDateTime?.toISOString() ?? '',
      registrationDeadline: formData.registrationDeadline?.toISOString() ?? '',
      type: formData.registrationType,
      location: formData.location,
      ...(formData.registrationType === 'free' && formData.maxAttendees
        ? { maxAttendeesLimit: Number(formData.maxAttendees) }
        : {}),
      specialInstructions,
      tickets: eventTicketData,
      timeZone: formData.timeZone,
      venueAddress: formData.venueAddress || '',
      venueCity: formData.venueCity || '',
      venueName: formData.venueName || '',
      venueState: formData.venueState || '',
      zoomLink: formData.zoomLink || '',
      guests: guestUsers,
      eventTags: formData.tag.map(tag => tag.id),
      venueLatitude: formData.venueLatitude
        ? Number(Number(formData.venueLatitude).toFixed(6))
        : undefined,
      venueLongitude: formData.venueLongitude
        ? Number(Number(formData.venueLongitude).toFixed(6))
        : undefined,
      venueCountry: formData.venueCountry || '',
      sponsorshipPackages,
      activityHighlights: eventHighlights,
      status: EVENT_FORM_STATUS.ACTIVE,
    }
  }

  const { mutate: createEventMutation, isPending: isCreatingEvent } =
    useMutation({
      mutationFn: createEvent,
      onSuccess: () => {
        allowNavigation()
        queryClient.invalidateQueries({ queryKey: [EVENT_QUERY_KEYS.all] })
        const eventName = getValues('eventName')
        showSuccessToast(
          'Event created successfully',
          `${eventName} has been added to the system and is ready for management.`
        )
        navigate(PATHROUTES.DASHBOARD.EVENT_MANAGEMENT)
      },
      onError: error => {
        if (axios.isAxiosError(error)) {
          const message =
            error?.response?.data?.message || 'Failed to create event'
          showErrorToast('Event Creation Failed', message)
        }
      },
    })

  const { mutate: updateEventMutation, isPending: isUpdatingEvent } =
    useMutation({
      mutationFn: updateEvent,
      onSuccess: () => {
        allowNavigation()
        queryClient.invalidateQueries({ queryKey: [EVENT_QUERY_KEYS.all] })
        queryClient.invalidateQueries({
          queryKey: [EVENT_QUERY_KEYS.byId(id!)],
        })
        const eventName = getValues('eventName')
        showSuccessToast(
          'Event updated successfully',
          `${eventName} has been updated`
        )
        navigate(PATHROUTES.DASHBOARD.EVENT_MANAGEMENT)
      },
      onError: error => {
        console.error('Update event error:', error)
        if (axios.isAxiosError(error)) {
          const message =
            error?.response?.data?.message || 'Failed to update event'
          showErrorToast('Event Update Failed', message)
        }
      },
    })

  const onSubmit = async (formData: EventFormDataType) => {
    const dateValidationPassed = validateFormDates(true)
    const hasHeroDurationError =
      eventPackageFormRef.current?.hasAnyPackageErrors()
    if (!dateValidationPassed || hasHeroDurationError) {
      showErrorToast(
        'Validation Error',
        'Please check and fix the highlighted fields.'
      )
      return
    }
    if (
      isUploadingEventImages ||
      isUploadingGuestImages ||
      isUploadingEventPdf
    ) {
      showErrorToast(
        'Upload in Progress',
        'Please wait for all files to finish uploading.'
      )
      return
    }

    if (formData.registrationType === 'paid') {
      const hasMainEvent = formData.eventTicket?.some(
        ticket => !ticket.isSubEventTicket
      )
      if (!hasMainEvent) {
        showErrorToast(
          'Validation Error',
          'At least one ticket must be a main event. Please uncheck "Sub Event" for at least one ticket.'
        )
        return
      }
    }

    try {
      const payload = await transformFormData(formData)
      if (id) {
        updateEventMutation({ id, payload })
      } else {
        createEventMutation(payload)
      }
    } catch (error) {
      console.error('Event submission error:', error)
      showErrorToast('Event Submission Failed', 'Failed to submit event')
    }
  }
  const handleSaveAsDraft = async () => {
    const ok = await form.trigger('eventName')
    if (!ok) {
      showErrorToast(
        'Validation Error',
        'Please provide an event name for the draft.'
      )
      return
    }

    const tickets = form.getValues('eventTicket')
    const registrationType = form.getValues('registrationType')
    const hasPaidTickets = validatePaidEventTickets(registrationType, tickets)

    if (!hasPaidTickets) {
      setPaidEventTicketErrors(form)
      showErrorToast(
        'Ticket Required',
        "Paid events require at least one complete ticket with all fields filled (name, description, what's included, and price)."
      )
      return
    }

    const guests = form.getValues('users')
    const { hasPartialGuests, incompleteIndexes: incompleteGuestIndexes } =
      validateDraftGuests(guests)

    if (hasPartialGuests) {
      setGuestErrors(form, incompleteGuestIndexes)
      showErrorToast(
        'Incomplete Guest Information',
        'Please complete all guest fields (name, designation, and profile image) or leave them all empty.'
      )
      return
    }

    const sponsorshipPackages = form.getValues('sponsorshipPackages')
    const { hasPartialPackages, incompleteIndexes: incompletePackageIndexes } =
      validateDraftPackages(sponsorshipPackages)

    if (hasPartialPackages) {
      setPackageErrors(form, incompletePackageIndexes)
      showErrorToast(
        'Incomplete Package Information',
        'Please complete all sponsorship package fields (name, description, price, ad slots, priority, and max sponsorships limit) or leave them empty.'
      )
      return
    }

    if (
      isUploadingEventImages ||
      isUploadingGuestImages ||
      isUploadingEventPdf
    ) {
      showErrorToast(
        'Upload in Progress',
        'Please wait for all files to finish uploading before saving draft.'
      )
      return
    }

    try {
      const values = form.getValues()

      // Upload PDF if present (new) or use existing PDF key
      const eventPdfPayload = await preparePdfPayload(
        values.eventPDF,
        uploadEventPdf
      )

      // Upload About Park City PDF if present (new) or use existing PDF key
      const eventInfoPdfPayload = await preparePdfPayload(
        values.eventInfoPDF,
        uploadEventInfoPdf
      )

      const payload = await buildDraftPayload(
        values,
        id,
        eventPdfPayload,
        eventInfoPdfPayload
      )

      allowNavigation()

      if (id) {
        updateEventMutation({ id, payload: payload as createEventPayload })
      } else {
        createEventMutation(payload as createEventPayload)
      }
    } catch (error) {
      console.error('Save draft error:', error)
      showErrorToast('Draft Save Failed', 'Failed to save draft.')
    }
  }

  const handleCancelClick = () => {
    allowNavigation()
    navigate(PATHROUTES.DASHBOARD.EVENT_MANAGEMENT)
  }

  if (id && (isLoadingEvent || isDataLoading)) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-30px)]">
        <CustomLoader />
        <span className="mt-40 ml-12">Loading event data...</span>
      </div>
    )
  }

  const isUploading =
    isUploadingEventImages ||
    isUploadingGuestImages ||
    isUploadingEventPdf ||
    isUploadingEventInfoPdf

  const onError = () => {
    validateFormDates(true)
    showErrorToast(
      'Validation Error',
      'Please check and fix the highlighted fields.'
    )
  }

  return (
    <>
      <div className="@container/main flex flex-1 flex-col gap-2.5 p-4 lg:p-6">
        {/* Breadcrumbs */}
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href={PATHROUTES.DASHBOARD.EVENT_MANAGEMENT}
                className="text-ss-neutral-600 hover:text-ss-blue-500 font-medium"
              >
                Event Management
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-ss-neutral-400" />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-ss-blue-500 font-medium">
                {id ? 'Edit' : 'Create'} Event
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-ss-neutral-950 leading-8">
              {id ? 'Edit Event' : 'Create New Event'}
            </h1>
            <p className="text-sm text-ss-neutral-600 leading-4.5 ">
              {id
                ? 'Update your event details, tickets, or settings. Changes will be reflected instantly for attendees.'
                : 'Fill in the details below to set up your event. You can always edit later.'}
            </p>
          </div>
        </div>

        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className="space-y-6 border border-ss-neutral-50 rounded-xl"
          >
            <Card className="border-none rounded-tl-xl rounded-tr-xl rounded-bl-none rounded-br-none bg-ss-neutral-white gap-0 pt-0 mb-0">
              <EventBasicInfoForm
                handleImageSelect={handleImageSelect}
                handleRemoveImage={handleRemoveImage}
                handleSelectPDF={handleSelectPDF}
                handleRemovePDF={handleRemovePDF}
                handleSelectInfoPDF={handleSelectInfoPDF}
                handleRemoveInfoPDF={handleRemoveInfoPDF}
                categoryData={categoryData?.data}
                tagData={tagData?.data}
              />
              <EventDateTimeForm />
              <EventLocationForm />
              <EventRegistrationForm totalTickets={data?.totalTickets || 0} />
              <EventPackageForm
                ref={eventPackageFormRef}
                startDate={form.watch('startDate')}
                endDate={form.watch('endDate')}
              />

              {/* Additional Information */}
              <CardHeader className="pb-4 border-t border-b border-black/6 bg-ss-neutral-25 flex flex-row items-center gap-2 px-4 py-3">
                <DocumentIcon className="h-5 w-5 text-ss-neutral-600" />
                <CardTitle className="text-lg font-semibold text-ss-neutral-950">
                  Additional Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="space-y-1">
                  <Label
                    variant="required"
                    htmlFor="specialInstructions"
                    className="text-sm font-semibold text-ss-neutral-950"
                  >
                    Special Instructions
                  </Label>
                  <Controller
                    name="specialInstructions"
                    control={form.control}
                    render={({ field, fieldState: { error } }) => (
                      <div className="relative">
                        <CustomRichTextEditor
                          value={field.value}
                          onChange={value =>
                            field.onChange(cleanSpecialInstructions(value))
                          }
                          placeholder="Any special instructions for attendees."
                          className={error ? 'border-ss-critical-200' : ''}
                        />
                        {error && (
                          <p className="text-sm text-ss-critical-200 mt-1">
                            {error.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </CardContent>
              <EventAddUserForm handleImageSelect={handleImageSelect} />
              <div className="bg-white border-t border-ss-neutral-50 rounded-b-xl px-6 py-4 shadow-[0px_5px_40px_0px_rgba(0,0,0,0.1)]">
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelClick}
                    disabled={
                      isCreatingEvent ||
                      isUpdatingEvent ||
                      isUploading ||
                      isUploadingHighlightImages
                    }
                    className="h-9 px-4 border-ss-neutral-200 text-ss-neutral-950 hover:bg-ss-neutral-50 font-semibold"
                  >
                    Cancel
                  </Button>

                  <div className="flex gap-3 items-center">
                    {eventData?.data?.status !== EVENT_FORM_STATUS.ACTIVE && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleSaveAsDraft}
                        disabled={
                          isCreatingEvent || isUpdatingEvent || isUploading
                        }
                        className="h-9 px-4 py-2.5 text-ss-blue-800 hover:bg-transparent font-semibold text-sm"
                      >
                        {isCreatingEvent || isUpdatingEvent || isUploading
                          ? 'Saving as Draft...'
                          : 'Save as Draft'}
                      </Button>
                    )}

                    <EventPreviewDrawer
                      event={form.watch()}
                      categoryData={categoryData?.data}
                      tagData={tagData?.data}
                      trigger={
                        <Button
                          type="button"
                          disabled={
                            isCreatingEvent || isUpdatingEvent || isUploading
                          }
                          className="h-9 px-4 bg-ss-blue-900 text-white hover:bg-ss-blue-800 disabled:bg-ss-neutral-300 font-semibold"
                        >
                          {isUploading ? 'Uploading...' : 'Preview'}
                        </Button>
                      }
                      isSubmitting={
                        isCreatingEvent ||
                        isUpdatingEvent ||
                        isUploading ||
                        isUploadingHighlightImages
                      }
                      handleSubmit={() => handleSubmit(onSubmit, onError)()}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </form>
        </FormProvider>
      </div>
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  )
}
